package com.starparty.app.vector_maths

import kotlin.math.abs

abstract class Tuple2d {
    var x: Double = 0.0
    var y: Double = 0.0

    constructor(x: Double, y: Double) {
        this.x = x
        this.y = y
    }

    constructor(t: DoubleArray) {
        x = t[0]
        y = t[1]
    }

    constructor(t1: Tuple2d) {
        x = t1.x
        y = t1.y
    }

    constructor() {
        x = 0.0
        y = 0.0
    }

    operator fun set(x: Double, y: Double) {
        this.x = x
        this.y = y
    }

    fun set(t: DoubleArray) {
        x = t[0]
        y = t[1]
    }

    fun set(t1: Tuple2d) {
        x = t1.x
        y = t1.y
    }

    operator fun get(t: DoubleArray) {
        t[0] = x
        t[1] = y
    }

    fun add(t1: Tuple2d, t2: Tuple2d) {
        x = t1.x + t2.x
        y = t1.y + t2.y
    }

    fun add(t1: Tuple2d) {
        x += t1.x
        y += t1.y
    }

    fun sub(t1: Tuple2d, t2: Tuple2d) {
        x = t1.x - t2.x
        y = t1.y - t2.y
    }

    fun sub(t1: Tuple2d) {
        x -= t1.x
        y -= t1.y
    }

    fun negate(t1: Tuple2d) {
        x = -t1.x
        y = -t1.y
    }

    fun negate() {
        x = -x
        y = -y
    }

    fun scale(s: Double, t1: Tuple2d) {
        x = s * t1.x
        y = s * t1.y
    }

    fun scale(s: Double) {
        x *= s
        y *= s
    }

    fun scaleAdd(s: Double, t1: Tuple2d, t2: Tuple2d) {
        x = s * t1.x + t2.x
        y = s * t1.y + t2.y
    }

    fun scaleAdd(s: Double, t1: Tuple2d) {
        x = s * x + t1.x
        y = s * y + t1.y
    }

    fun equals(t1: Tuple2d): Boolean {
        return try {
            x == t1.x && y == t1.y
        } catch (var3: NullPointerException) {
            false
        }
    }

    override fun equals(t1: Any?): Boolean {
        return try {
            val t2 = t1 as Tuple2d?
            x == t2!!.x && y == t2.y
        } catch (var3: NullPointerException) {
            false
        } catch (var4: ClassCastException) {
            false
        }
    }

    fun epsilonEquals(t1: Tuple2d, epsilon: Double): Boolean {
        var diff = x - t1.x
        return if (diff.isNaN()) {
            false
        } else if ((if (diff < 0.0) -diff else diff) > epsilon) {
            false
        } else {
            diff = y - t1.y
            if (diff.isNaN()) {
                false
            } else {
                (if (diff < 0.0) -diff else diff) <= epsilon
            }
        }
    }

    override fun toString(): String {
        return "(" + x + ", " + y + ")"
    }

    fun clamp(min: Double, max: Double, t: Tuple2d) {
        if (t.x > max) {
            x = max
        } else if (t.x < min) {
            x = min
        } else {
            x = t.x
        }
        if (t.y > max) {
            y = max
        } else if (t.y < min) {
            y = min
        } else {
            y = t.y
        }
    }

    fun clampMin(min: Double, t: Tuple2d) {
        if (t.x < min) {
            x = min
        } else {
            x = t.x
        }
        if (t.y < min) {
            y = min
        } else {
            y = t.y
        }
    }

    fun clampMax(max: Double, t: Tuple2d) {
        if (t.x > max) {
            x = max
        } else {
            x = t.x
        }
        if (t.y > max) {
            y = max
        } else {
            y = t.y
        }
    }

    fun absolute(t: Tuple2d) {
        x = abs(t.x)
        y = abs(t.y)
    }

    fun clamp(min: Double, max: Double) {
        if (x > max) {
            x = max
        } else if (x < min) {
            x = min
        }
        if (y > max) {
            y = max
        } else if (y < min) {
            y = min
        }
    }

    fun clampMin(min: Double) {
        if (x < min) {
            x = min
        }
        if (y < min) {
            y = min
        }
    }

    fun clampMax(max: Double) {
        if (x > max) {
            x = max
        }
        if (y > max) {
            y = max
        }
    }

    fun absolute() {
        x = abs(x)
        y = abs(y)
    }

    fun interpolate(t1: Tuple2d, t2: Tuple2d, alpha: Double) {
        x = (1.0 - alpha) * t1.x + alpha * t2.x
        y = (1.0 - alpha) * t1.y + alpha * t2.y
    }

    fun interpolate(t1: Tuple2d, alpha: Double) {
        x = (1.0 - alpha) * x + alpha * t1.x
        y = (1.0 - alpha) * y + alpha * t1.y
    }

}